/**
 * Team-scoped login portal.
 *
 * Same backend (`/api/admin/login`) but branded for a specific system
 * (Studio / Campaigns). After successful sign-in we call the smart-landing
 * service which routes the user to their portal:
 *   - studio team member  → /studio
 *   - campaign team       → /my-campaigns
 *   - super admin         → /admin
 *
 * If the caller isn't a member of the system they're trying to log in to,
 * we still complete the login (auth succeeded) but surface a friendly
 * "you don't have access to <SYSTEM> yet" hint before redirecting.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import afterLogin from '@/lib/afterLogin';


/**
 * Reusable team-login component.
 *
 * Props:
 *   system: 'studio' | 'campaign'
 *   title:  string
 *   subtitle: string
 *   icon:   lucide icon component
 *   accent: tailwind colour (e.g. 'violet', 'emerald')
 *   membershipCheckUrl: api path returning 200 if the caller has access
 *   defaultLanding:     fallback URL if landing service fails
 */
export const TeamLoginPortal = ({
  system,
  title,
  subtitle,
  icon: Icon,
  accent = 'violet',
  membershipCheckUrl,
  defaultLanding,
}) => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  const loginMutation = useMutation({
    mutationFn: (data) => api.post('/admin/login', data),
    onSuccess: async (response) => {
      const { access_token, admin, permissions } = response.data;
      setAuth(access_token, admin || { email }, 'admin', null, permissions || []);

      // Soft access check — does this user actually have a role in this system?
      let hasAccess = true;
      let isSuper = false;
      try {
        const r = await api.get(membershipCheckUrl);
        // /studio/team/me  returns an object (or 404)
        // /campaigns/team/mine returns an array (empty = no access)
        if (Array.isArray(r.data)) {
          hasAccess = r.data.length > 0;
        } else if (r.data && typeof r.data === 'object') {
          hasAccess = !!r.data;
          isSuper = !!(r.data.is_super || r.data.role === 'super_admin');
        } else {
          hasAccess = false;
        }
        // Super admins always pass team-login access checks even if they're
        // not literally in the team collection.
        if (admin && (admin.is_super_admin || admin.role === 'super_admin' || (permissions || []).includes('*'))) {
          hasAccess = true;
          isSuper = true;
        }
      } catch (e) {
        hasAccess = false;
      }

      if (!hasAccess) {
        toast.warning(
          `Signed in, but you're not on the ${title} team yet. Ask an admin to add you.`,
          { duration: 6000 }
        );
        navigate('/admin', { replace: true });
        return;
      }

      toast.success(`Welcome back to ${title}!`);
      // Honour redirect param, otherwise let the smart-landing decide. Super
      // admins logging in through this portal still go to the team page
      // (they explicitly chose this entry point).
      const redirectParam = new URLSearchParams(window.location.search).get('redirect') || '';
      if (redirectParam) {
        navigate(redirectParam, { replace: true });
      } else if (isSuper) {
        navigate(defaultLanding, { replace: true });
      } else {
        await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect: '' });
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Email and password required');
      return;
    }
    loginMutation.mutate({ email: email.trim().toLowerCase(), password });
  };

  const accentBg = {
    violet:  'from-violet-600 to-fuchsia-600',
    emerald: 'from-emerald-600 to-teal-600',
    red:     'from-red-600 to-rose-600',
    amber:   'from-amber-500 to-orange-500',
    indigo:  'from-indigo-600 to-blue-600',
    teal:    'from-teal-600 to-emerald-600',
  }[accent] || 'from-slate-700 to-slate-900';
  const accentRing = {
    violet:  'ring-violet-200',
    emerald: 'ring-emerald-200',
    red:     'ring-red-200',
    amber:   'ring-amber-200',
    indigo:  'ring-indigo-200',
    teal:    'ring-teal-200',
  }[accent] || 'ring-slate-200';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid={`${system}-login-portal`}>
      <div className="w-full max-w-md">
        {/* Branded header */}
        <div className="text-center mb-8">
          <div className={`inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br ${accentBg} items-center justify-center text-white shadow-lg ring-4 ${accentRing} mb-4`}>
            <Icon className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor={`${system}-email`} className="text-xs">Email</Label>
                <Input
                  id={`${system}-email`}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  data-testid={`${system}-login-email`}
                />
              </div>
              <div>
                <Label htmlFor={`${system}-password`} className="text-xs">Password</Label>
                <div className="relative">
                  <Input
                    id={`${system}-password`}
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    data-testid={`${system}-login-password`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className={`w-full bg-gradient-to-br ${accentBg} hover:opacity-95`}
                disabled={loginMutation.isPending}
                data-testid={`${system}-login-submit`}
              >
                {loginMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in…</>
                ) : (
                  <>Sign in to {title}</>
                )}
              </Button>

              <div className="text-center text-xs text-slate-400 pt-2">
                <Link to={`/forgot-password?type=admin&next=${encodeURIComponent(`/${system}/login`)}`} className="hover:underline" data-testid={`${system}-forgot-link`}>Forgot password?</Link>
                <span className="mx-2">·</span>
                <Link to="/login" className="hover:underline">General sign-in</Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-slate-400 mt-6">
          You'll only see {title.toLowerCase()} workspaces you've been added to.
        </p>
      </div>
    </div>
  );
};

export default TeamLoginPortal;
